import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import ContentLoader from 'react-content-loader';
import {
  BackgroundContainer,
  Container,
  ListView,
  MainHeading,
  GridContainer,
  TransactionCard,
  TransactionCode,
  TransactionDetails,
  PropertyLocation,
  TransactionStatus,
  StatusOutliner,
  PendingText,
  TransactionUser,
  CreatedText,
  LastUpdatedText,
  Footer,
  SeeMoreText,
  SeeMoreContainer,
  Loader,
  NoDataCard,
  NoDataText,
  ListContainer,
  MobileHeadingContainer,
  MobileHeading,
  MobileFilterContainer,
  MobileCreate,
  MobileContainer,
  DesktopSideNav,
  StyledMenu,
  SearchHeading,
  SearchContainer,
  SearchText,
  TransactionButton,
} from '@app/modules/transactions/transactions-components';
import { Button, StatusBar, ToolTip } from '@app/components';
import { useLocation } from '@app/utils';
import { TransactionData, TransactionFilters } from '@app/types';
import SideNav from './transactions-sidenav';
import { AuthContext } from '@app/providers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSlidersH, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useGetTransactionsQuery } from '@app/graphql';
import { createTransaction } from '@app/utils/permissions';
import DefaultErroPage from '@app/components/error-pages/default-error-page';

const IconStyle = {
  width: 20,
  height: 20,
};

const TransactionLoader = ({ count }: { count: number }) => {
  const dummy = Array.from(Array(count));
  return (
    <>
      {dummy.map((x, i) => {
        return (
          <Loader key={i}>
            <ContentLoader
              speed={2}
              style={{ width: '100%', height: '75px' }}
              backgroundColor="#cccccc8f"
              foregroundColor="#e1e3e8">
              <rect x="0" y="0" rx="3" ry="3" width="80" height="12" />
              <rect x="0" y="20" rx="3" ry="3" width="500" height="25" />
              <rect x="725" y="20" rx="3" ry="3" width="90" height="20" />
              <rect x="830" y="20" rx="3" ry="3" width="90" height="20" />
              <rect x="0" y="55" rx="3" ry="3" width="400" height="14" />
            </ContentLoader>
          </Loader>
        );
      })}
    </>
  );
};

const PAGE_LENGTH: number = 20;
const Transactions = () => {
  const { location, navigate } = useLocation();
  const { userInfo, setUserInfo }: any = useContext(AuthContext);
  const { searchText }: any = useParams();
  const [transactionData, setTransactionData] = React.useState<
    TransactionData[]
  >([]);
  const [totalRecords, setTotalRecords] = React.useState<string>('');
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [totalPages, setTotalPages] = React.useState<number>(1);
  const [seeMoreloading, setSeeMoreLoading] = React.useState<boolean>(false);
  const [openSideNav, setOpenSideNav] = React.useState<boolean>(false);
  const [showErroPage, setShowErrorPage] = React.useState<boolean>(false);
  const [filters, setFilters] = React.useState<TransactionFilters>({
    municipalities: '',
    createdDateRange: {
      startDate: moment()
        .subtract(30, 'day')
        .format('YYYY-MM-DD'),
      endDate: moment()
        .add(1, 'day')
        .format('YYYY-MM-DD'),
    },
    status: '',
  });
  const { id: firebaseId = '', municipalityId = '', userType = '' } = userInfo;
  const { loading, error, data } = useGetTransactionsQuery({
    type: location.pathname.includes('/approved') ? 'approved' : 'current',
    firebaseId:
      userType && userType.toLowerCase() === 'professional_user'
        ? firebaseId
        : undefined,
    municipalityId:
      userType && userType.toLowerCase() === 'municipality_user'
        ? municipalityId
        : undefined,
    superAdmin: userType === 'super_admin',
    filters: filters,
    page: currentPage,
    length: PAGE_LENGTH,
    sort: '',
    search: searchText || '',
    offSetData: new Date().getTimezoneOffset().toString(),
  });

  const updateTransactionData = (newTransactionData: TransactionData[]) => {
    setTransactionData(transactionData.concat(newTransactionData));
  };

  const resetTransactionData = () => {
    setTotalRecords('');
    setCurrentPage(1);
    setTransactionData([]);
  };

  function usePrevious(value: any) {
    const ref = React.useRef();
    React.useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const prevSearchText = usePrevious(searchText);

  React.useEffect(() => {
    if (
      !loading &&
      !error &&
      data &&
      data.getTransactions &&
      data.getTransactions.success
    ) {
      setSeeMoreLoading(false);
      const {
        totalPages: totalDBPages = 0,
        totalRecords: totalDBRecords = 0,
        transaction = [],
      } = data.getTransactions;
      setTotalPages(totalDBPages);
      setTotalRecords(totalDBRecords);
      updateTransactionData(transaction);
    } else if (!loading && error && error.networkError) {
      setShowErrorPage(true);
    }
    if (prevSearchText !== searchText && searchText) {
      resetTransactionData();
    }
    if (
      !loading &&
      error &&
      error.networkError &&
      error.networkError.message &&
      error.networkError.message ===
        'Response not successful: Received status code 400'
    ) {
      localStorage.removeItem('token');
      setUserInfo(null);
      setTimeout(() => {
        navigate('/sign-in');
      }, 200);
    }

    // eslint-disable-next-line
  }, [loading, data]);

  const applyFilters = (appliedFilters: TransactionFilters) => {
    if (JSON.stringify(filters) !== JSON.stringify(appliedFilters)) {
      resetTransactionData();
      setFilters(appliedFilters);
      setOpenSideNav(false);
    }
  };
  const resetFilters = (appliedFilters: TransactionFilters) => {
    if (JSON.stringify(filters) !== JSON.stringify(appliedFilters)) {
      resetTransactionData();
      setFilters(appliedFilters);
      setOpenSideNav(false);
    }
  };

  const handleRowClicked = (data: any) => {
    navigate(`/transactions/details/${data.id}`);
  };

  const loadMore = () => {
    if (currentPage < totalPages) {
      setSeeMoreLoading(true);
      setCurrentPage(currentPage + 1);
    }
  };

  const getTitle = (totalRecords: string) => {
    let title: string = '';
    if (location.pathname.includes('/transactions/approved')) {
      title =
        totalRecords && parseInt(totalRecords) > 1
          ? `${totalRecords} Approved Transactions`
          : `${totalRecords} Approved Transaction`;
    } else if (location.pathname.includes('/transactions/documents')) {
      title =
        totalRecords && parseInt(totalRecords) > 1
          ? `${totalRecords} All Documents`
          : `${totalRecords} All Documents`;
    } else {
      title =
        totalRecords && parseInt(totalRecords) > 1
          ? `${totalRecords} Current Transactions`
          : `${totalRecords} Current Transaction`;
    }

    return title;
  };

  return (
    <>
      {showErroPage ? (
        <DefaultErroPage message={'Something went wrong.Please try again...'} />
      ) : (
        <BackgroundContainer>
          <Container>
            <DesktopSideNav>
              <SideNav
                filters={filters}
                applyFilters={applyFilters}
                isListLoading={loading}
                resetFilters={resetFilters}
              />
            </DesktopSideNav>

            <StyledMenu open={openSideNav}>
              <SideNav
                filters={filters}
                applyFilters={applyFilters}
                isListLoading={loading}
                resetFilters={resetFilters}
                closeSideNav={() => {
                  setOpenSideNav(false);
                }}
              />
            </StyledMenu>

            <ListView>
              {totalRecords && searchText ? (
                <SearchContainer>
                  <SearchHeading>Showing search results for</SearchHeading>
                  <SearchText>' {searchText} '</SearchText>
                </SearchContainer>
              ) : (
                ''
              )}
              <MainHeading>
                {loading && !seeMoreloading ? (
                  <ContentLoader
                    speed={2}
                    style={{ width: '350px', height: '35px' }}
                    backgroundColor="#cccccc8f"
                    foregroundColor="#e1e3e8">
                    <rect x="0" y="0" rx="3" ry="3" width="30" height="25" />
                    <rect x="40" y="0" rx="3" ry="3" width="350" height="25" />
                  </ContentLoader>
                ) : (
                  <div>{totalRecords ? getTitle(totalRecords) : ''}</div>
                )}
                {createTransaction(userInfo) ? (
                  <TransactionButton>
                    <Button
                      variant="warning"
                      size="medium"
                      type="submit"
                      onClick={() => navigate('/transactions/create')}>
                      New Transaction
                    </Button>
                  </TransactionButton>
                ) : null}
              </MainHeading>
              <MobileHeadingContainer>
                <MobileHeading>
                  {loading && !seeMoreloading ? (
                    <ContentLoader
                      speed={2}
                      style={{ width: '200px', height: '20px' }}
                      backgroundColor="#ccc"
                      foregroundColor="#a5a8add1">
                      <rect x="0" y="0" rx="3" ry="3" width="30" height="25" />
                      <rect
                        x="40"
                        y="0"
                        rx="3"
                        ry="3"
                        width="350"
                        height="25"
                      />
                    </ContentLoader>
                  ) : (
                    <>{totalRecords ? getTitle(totalRecords) : ''}</>
                  )}
                </MobileHeading>
                <MobileFilterContainer
                  onClick={() => {
                    setOpenSideNav(true);
                  }}>
                  <FontAwesomeIcon
                    color="black"
                    icon={faSlidersH}
                    style={IconStyle}
                  />
                </MobileFilterContainer>
              </MobileHeadingContainer>
              {loading && !seeMoreloading && <TransactionLoader count={5} />}
              <ListContainer>
                {!loading && !totalRecords && (
                  <NoDataCard>
                    <NoDataText>No transactions to display</NoDataText>
                  </NoDataCard>
                )}
                {transactionData.map((data, i) => {
                  return (
                    <GridContainer
                      onClick={() => handleRowClicked(data)}
                      key={i}>
                      <TransactionCard>
                        <TransactionCode>
                          {data.transactionCode ? data.transactionCode : '- -'}
                        </TransactionCode>
                        <TransactionDetails>
                          <PropertyLocation
                            data-tip
                            data-for={`property[${i}]`}>
                            {data &&
                              `${data.property.address}, ${data.property.city}, ${data.property.state}, ${data.property.zipCode}`}
                          </PropertyLocation>
                          <ToolTip
                            id={`property[${i}]`}
                            text={
                              data &&
                              `${data.property.address}, ${data.property.city}, ${data.property.state}, ${data.property.zipCode}`
                            }
                          />
                          <TransactionStatus>
                            <StatusOutliner>
                              <PendingText>
                                {data.pendingTasks
                                  ? data.pendingTasks > 1
                                    ? data.pendingTasks + ' Tasks Pending'
                                    : data.pendingTasks + ' Task Pending'
                                  : ''}
                              </PendingText>
                            </StatusOutliner>
                            <StatusOutliner>
                              <StatusBar
                                variant={
                                  data.status === 'INPROGRESS'
                                    ? 'magento'
                                    : data.status === 'OPEN'
                                    ? 'primary'
                                    : 'success'
                                }>
                                {data.status === 'COMPLETED'
                                  ? 'REQUESTED FOR APPROVAL'
                                  : data.status}
                              </StatusBar>
                            </StatusOutliner>
                          </TransactionStatus>
                        </TransactionDetails>
                        <TransactionUser>
                          <CreatedText>By: {data.createdBy}</CreatedText>
                          <LastUpdatedText>
                            Last Updated:{' '}
                            {data.updatedAt
                              ? moment(data.updatedAt)
                                  // .utc()
                                  .format('MMM D, YYYY')
                              : '- -'}
                          </LastUpdatedText>
                        </TransactionUser>
                      </TransactionCard>
                    </GridContainer>
                  );
                })}

                {totalRecords && currentPage < totalPages ? (
                  seeMoreloading ? (
                    <TransactionLoader count={1} />
                  ) : (
                    <Footer>
                      <SeeMoreContainer onClick={loadMore}>
                        <SeeMoreText>See More</SeeMoreText>
                      </SeeMoreContainer>
                    </Footer>
                  )
                ) : null}
              </ListContainer>
              {createTransaction(userInfo) && (
                <MobileContainer>
                  <MobileCreate
                    onClick={() => navigate('/transactions/create')}>
                    <FontAwesomeIcon
                      color="#fff"
                      icon={faPlus}
                      style={IconStyle}
                    />
                  </MobileCreate>
                </MobileContainer>
              )}
            </ListView>
          </Container>
        </BackgroundContainer>
      )}
    </>
  );
};

export default Transactions;
